import { createCompanyPeopleHeroContent } from './companyPeopleHeroContent.js';
import { companyHeroCopy, companyObjectionsCopy } from './companyHomeContent.js';
import { createMatchingContent } from './landingMatchingContent.js';
import type { LandingContent } from './landingContentTypes.js';
import { globusCampaignContent } from './landingContentGlobusCampaign.js';
import * as shared from './landingContentShared.js';
const { brandAssetRepository, industryCare, industryCraft, industryRetail, heroPflege, heroHandwerk, heroHandel, heroUnternehmenBedarf, heroUnternehmenBranchen, heroUnternehmenDokumente, heroUnternehmenIntegration, partnerGlobus, partnerTempusleben, partnerEmek, teamGerdSchneider, groupPhoto, assets, contact } = shared;

export const deContent: LandingContent = {
  locale: 'de',
  languageLabel: 'Deutsch',
  brandLogo: brandAssetRepository.navbarLogoDataUri,
  nav: [
    { label: 'Pflege', href: '/branchen/pflege' },
    { label: 'Einzelhandel', href: '/branchen/einzelhandel' },
    { label: 'Handwerk', href: '/branchen/handwerk' },
  ],
  hero: {
    eyebrow: 'Internationale Fachkräftevermittlung mit Verantwortung',
    title: 'Viva Porta verbindet Menschen',
    highlight: 'mit Zukunft',
    videoUrl: '',
    intro: 'Wir bringen Unternehmen in Deutschland mit motivierten internationalen Fachkräften zusammen. Strukturiert, persönlich begleitet und mit Blick auf langfristiges Ankommen.',
    bullets: ['Branchenlösungen für Pflege, Einzelhandel und Handwerk', 'Persönliche Begleitung für Unternehmen und Bewerber:innen', 'Klare Prozesse rund um Auswahl, Vorbereitung, Formalitäten und Integration'],
    primaryAction: { label: 'Für Bewerber:innen', href: contact.application },
    secondaryAction: { label: 'Branchen ansehen', href: '#branchen' },
    imageAlt: 'Menschen in einem modernen Arbeitsumfeld',
    trustLabels: [],
  },
  heroApplicant: {
    badge: 'Schließe dich 1.500+ Pflegekräften an',
    avatars: [assets.nursePortrait1, assets.nursePortrait2, assets.nursePortrait3],
    title: 'Mehr Zukunft. Mehr Sicherheit. Mehr du.',
    highlight: 'Mehr du.',
    intro: 'Wir begleiten Pflegekräfte aus der Türkei persönlich nach Deutschland – Schritt für Schritt.',
    trustLabel: 'Trustpilot 4,9 / 5 · über 320 Bewertungen',
    cards: [
      {
        eyebrow: 'Sprache',
        title: 'Sicher Deutsch sprechen',
        image: assets.nursePortrait1,
        imageAlt: 'Pflegekraft lernt Deutsch',
        href: '/ablauf',
      },
      {
        eyebrow: 'Anerkennung',
        title: 'Deinen Beruf anerkennen',
        image: assets.nursePortrait2,
        imageAlt: 'Pflegekraft mit anerkannten Dokumenten',
        href: '/ablauf',
      },
      {
        eyebrow: 'Ankommen',
        title: 'In Deutschland ankommen',
        image: assets.nursePortrait3,
        imageAlt: 'Pflegekraft im neuen Arbeitsumfeld',
        href: contact.application,
      },
    ],
  },
  heroCompany: {
    badge: 'Über 40 Einrichtungen vertrauen uns',
    avatars: [assets.nursePortrait3, assets.nursePortrait1, assets.nursePortrait2],
    title: 'Mehr Fachkräfte. Mehr Sicherheit. Mehr Team.',
    highlight: 'Mehr Team.',
    intro: 'Geprüfte Pflegefachkräfte aus der Türkei – rechtssicher vermittelt und voll begleitet.',
    trustLabel: 'Rechtssicher · Anerkennungsbegleitung inklusive',
    cards: [
      {
        eyebrow: 'Fachkräfte',
        title: 'Geprüfte Pflegekräfte',
        image: assets.nursePortrait2,
        imageAlt: 'Geprüfte Pflegefachkraft',
        href: contact.medical,
      },
      {
        eyebrow: 'Rechtssicher',
        title: 'Sorgenfrei einstellen',
        image: assets.nursePortrait1,
        imageAlt: 'Pflegekraft im Klinikalltag',
        href: '/kontakt',
      },
      {
        eyebrow: 'Begleitung',
        title: 'Voll begleitet',
        image: assets.nursePortrait3,
        imageAlt: 'Pflegekraft in Begleitung',
        href: '/ablauf',
      },
    ],
  },
  heroFanApplicant: {
    badge: 'Für Bewerber:innen aus dem Ausland',
    avatars: [industryCare, industryRetail, industryCraft],
    title: 'Dein beruflicher Start in Deutschland beginnt hier.',
    highlight: 'beginnt hier.',
    intro: 'Du möchtest in Deutschland arbeiten und suchst einen seriösen Weg? Viva Porta zeigt dir passende Branchen, erklärt die nächsten Schritte und begleitet dich persönlich - von der Orientierung bis zum Ankommen.',
    trustLabel: '',
    cards: [
      {
        eyebrow: 'Pflege',
        title: 'Als Pflegefachkraft starten',
        image: heroPflege,
        imageAlt: 'Pflegefachkraft in einer hellen modernen Einrichtung',
        href: '/branchen/pflege',
      },
      {
        eyebrow: 'Einzelhandel',
        title: 'Im Einzelhandel starten',
        image: heroHandel,
        imageAlt: 'Mitarbeiterin in einem modernen Einzelhandelsgeschäft',
        href: '/branchen/einzelhandel',
      },
      {
        eyebrow: 'Handwerk',
        title: 'Im Handwerk anpacken',
        image: heroHandwerk,
        imageAlt: 'Fachkraft in einer hellen Werkstatt',
        href: '/branchen/handwerk',
      },
    ],
  },
  heroFanCompany: {
    badge: 'Für Arbeitgeber in Deutschland',
    avatars: [industryCare, industryRetail, industryCraft],
    title: 'Fachkräfte gewinnen, die wirklich ins Team passen.',
    highlight: 'ins Team passen.',
    intro: 'Sie suchen verlässliche internationale Mitarbeitende? Viva Porta klärt Ihren Bedarf, prüft Profile, koordiniert Formalitäten und begleitet das Ankommen im Betrieb.',
    trustLabel: '',
    cards: [
      {
        eyebrow: 'Bedarf',
        title: 'Personalbedarf sauber klären',
        image: heroUnternehmenBedarf,
        imageAlt: 'Beratungsgespräch mit einem Arbeitgeber',
        href: '#kontakt',
        quote: 'Schildern Sie uns Ihre offenen Rollen. Wir ordnen Bedarf, Zeitplan und realistische nächste Schritte gemeinsam ein.',
        quoteAuthor: 'Personalbedarf klären',
        quoteRole: 'Bedarf besprechen',
      },
      {
        eyebrow: 'Branchen',
        title: 'Profile für reale Rollen',
        image: heroUnternehmenBranchen,
        imageAlt: 'Service-Team in einem modernen Betrieb',
        href: '#branchen',
        quote: 'Entdecken Sie unsere Branchenlösungen und erfahren Sie, wie wir passende Profile für konkrete Aufgaben vorbereiten.',
        quoteAuthor: 'Passende Profile finden',
        quoteRole: 'Branchen ansehen',
      },
      {
        eyebrow: 'Formalitäten',
        title: 'Dokumente koordiniert',
        image: heroUnternehmenDokumente,
        imageAlt: 'Dokumente und digitale Planung für internationale Einstellung',
        href: '#ablauf',
        quote: 'Erfahren Sie, wie Auswahl, Dokumente, Einreise und Arbeitsstart transparent aufeinander abgestimmt werden.',
        quoteAuthor: 'Prozess sicher planen',
        quoteRole: 'Ablauf ansehen',
      },
      {
        eyebrow: 'Integration',
        title: 'Ankommen im Team begleiten',
        image: heroUnternehmenIntegration,
        imageAlt: 'Internationale Fachkraft wird im Team begrüßt',
        href: '#kontakt',
        quote: 'Lassen Sie uns besprechen, wie Vorbereitung, Onboarding und persönliche Begleitung Ihr Team langfristig stärken.',
        quoteAuthor: 'Ankommen gemeinsam gestalten',
        quoteRole: 'Kontakt aufnehmen',
      },
    ],
  },
  companyPlatformHero: { ...companyHeroCopy.de, matching: createMatchingContent('de') },
  companyPeopleHero: createCompanyPeopleHeroContent('de'),
  companyObjections: companyObjectionsCopy.de, globusCampaignApplicant: globusCampaignContent.de.applicant, globusCampaignCompany: globusCampaignContent.de.company,
  intro: {
    eyebrow: 'Über Viva Porta',
    title: 'Dein Weg nach Deutschland, persönlich unterstützt',
    titleHighlight: 'unterstützt',
    text: 'Wir verbinden Viva Portas Erfahrung mit Visa- und Relocation-Prozessen mit fundiertem Wissen über internationale Berufswege. So entsteht ein klar strukturierter Weg für Menschen, die in Deutschland eine berufliche Zukunft aufbauen möchten.',
    metrics: [
      { value: '360°', label: 'Unterstützung bei Sprache, Anerkennung, Visum und Ankommen' },
      { value: '2', label: 'Zwei Kulturen, ein Team: Deutschland und Türkei' },
      { value: '24/7', label: 'Erreichbare Begleitung in kritischen Phasen' },
    ],
  },
  industryOverview: {
    eyebrow: 'Branchen',
    title: 'Nicht nur Fachkräftevermittlung: Wir bringen die richtigen Menschen zu Ihnen.',
    titleHighlight: 'richtigen Menschen',
    text: 'Viva Porta verbindet Unternehmen und internationale Bewerber:innen individuell, persönlich und exklusiv. Wir schauen nicht nur auf Abschlüsse, sondern auf Menschen, Teams, Erwartungen und die Frage, ob eine Zusammenarbeit wirklich langfristig tragen kann.',
    items: [
      {
        iconClass: 'icon-[material-symbols--medical-services]',
        title: 'Pflege',
        text: 'Für Kliniken, Pflegeeinrichtungen und ambulante Dienste, die qualifizierte Pflegefachkräfte langfristig integrieren möchten.',
        href: '/branchen/pflege',
        linkLabel: 'Pflege ansehen',
      },
      {
        iconClass: 'icon-[material-symbols--storefront]',
        title: 'Einzelhandel',
        text: 'Für Globus, Frischetheken, Verkauf und serviceorientierte Teams mit dauerhaftem Personalbedarf.',
        href: '/branchen/einzelhandel',
        linkLabel: 'Einzelhandel ansehen',
      },
      {
        iconClass: 'icon-[material-symbols--construction]',
        title: 'Handwerk',
        text: 'Für Handwerksbetriebe, die Nachwuchs- und Fachkräfteengpässe strukturiert und menschlich lösen wollen.',
        href: '/branchen/handwerk',
        linkLabel: 'Handwerk ansehen',
      },
    ],
  },
  laborShortage: {
    eyebrow: 'Fachkräftemangel verstehen',
    title: 'Wenn offene Stellen zum Risiko werden',
    titleHighlight: 'Risiko',
    text: 'Unsere Mission ist nicht, aus Personalnot kurzfristig Geschäft zu machen. Viva Porta will ein gesellschaftliches Problem lösen: Unternehmen brauchen verlässliche Menschen, internationale Bewerber:innen brauchen faire Chancen, und beide Seiten brauchen eine Begleitung, die menschlich bleibt.',
    stats: [
      {
        value: '3,2 Mio.',
        label: 'Menschen weniger im Erwerbsalter bis Mitte der 2030er-Jahre selbst bei hoher Nettozuwanderung.',
        sourceLabel: 'Destatis, 2025',
        sourceHref: 'https://www.destatis.de/DE/Im-Fokus/Fachkraefte/Demografie/_inhalt.html',
      },
      {
        value: '400.000',
        label: 'jährliche Nettozuwanderung nennt das IAB als Größenordnung, damit das Arbeitskräfteangebot langfristig nahezu konstant bleibt.',
        sourceLabel: 'IAB',
        sourceHref: 'https://iab.de/presseinfo/nur-mit-einer-jaehrlichen-nettozuwanderung-von-400-000-personen-bleibt-das-arbeitskraefteangebot-langfristig-konstant/',
      },
      {
        value: '768.000',
        label: 'Stellen könnten laut IW-Ausblick bis 2028 nicht qualifiziert besetzt werden, wenn Angebot und Nachfrage weiter auseinanderlaufen.',
        sourceLabel: 'IW / iwd',
        sourceHref: 'https://www.iwd.de/artikel/die-fachkraefteluecke-waechst-weiter-658100/',
      },
    ],
    points: [
      {
        iconClass: 'icon-[material-symbols--volunteer-activism]',
        title: 'Mission',
        text: 'Wir verbinden Unternehmen in Deutschland mit engagierten, qualifizierten Menschen und begegnen dem Fachkräftemangel nachhaltig.',
      },
      {
        iconClass: 'icon-[material-symbols--diversity-1]',
        title: 'Ansatz',
        text: 'Deutsch-türkische Wurzeln, persönliche Begleitung und gewachsenes Vertrauen schaffen Verbindungen, die fachlich und menschlich tragen.',
      },
      {
        iconClass: 'icon-[material-symbols--handshake]',
        title: 'Unsere Geschichte',
        text: 'Grenzen überwinden, Menschen verbinden. Seit 2025 vereinen Enes Vural, Wilhelm Josten, Murat Vural und Serife Vural-Banik unternehmerische und soziale Erfahrung.',
      },
    ],
    sources: [
      {
        label: 'Bundesagentur für Arbeit: Engpassanalyse',
        href: 'https://statistik.arbeitsagentur.de/DE/Navigation/Statistiken/Interaktive-Statistiken/Fachkraeftebedarf/Engpassanalyse-Nav.html',
      },
      {
        label: 'Destatis: Demografischer Wandel',
        href: 'https://www.destatis.de/DE/Im-Fokus/Fachkraefte/Demografie/_inhalt.html',
      },
      {
        label: 'IAB: Arbeitskräfteangebot und Zuwanderung',
        href: 'https://iab.de/presseinfo/nur-mit-einer-jaehrlichen-nettozuwanderung-von-400-000-personen-bleibt-das-arbeitskraefteangebot-langfristig-konstant/',
      },
      {
        label: 'IW: Fachkräftelücke wächst weiter',
        href: 'https://www.iwd.de/artikel/die-fachkraefteluecke-waechst-weiter-658100/',
      },
    ],
  },
  reasons: {
    eyebrow: 'Warum Viva Porta?',
    title: 'Starke Gründe für eine Pflegekarriere in Deutschland.',
    titleHighlight: 'Deutschland',
    image: assets.article,
    text: 'Deutschland bietet Sicherheit, moderne Gesundheitsstrukturen und echte Karriereperspektiven. Viva Porta macht daraus einen planbaren Neustart.',
    cards: [
      {
        iconClass: 'icon-[material-symbols--groups]',
        title: 'Starke Community',
        text: 'Eine große türkische Community erleichtert den Start im Alltag, im Team und im sozialen Umfeld.',
      },
      {
        iconClass: 'icon-[material-symbols--flight-takeoff]',
        title: 'Nahe an der Heimat',
        text: 'Viele Direktverbindungen zwischen Deutschland und der Türkei halten Familie und neues Leben nah beieinander.',
      },
      {
        iconClass: 'icon-[material-symbols--verified-user]',
        title: 'Sicherheit und Fairness',
        text: 'Wir achten auf faire Bedingungen, transparente Kommunikation und eine Vermittlung, die zu deinem Profil passt.',
      },
    ],
  },
  benefits: {
    eyebrow: 'Vorteile auf einen Blick',
    title: 'Was du von uns erwarten kannst.',
    titleHighlight: 'erwarten',
    items: [
      {
        iconClass: 'icon-[material-symbols--school]',
        title: 'Sprache und Weiterbildung',
        text: 'Begleitung bis zum passenden Sprachniveau, inklusive Vorbereitung auf medizinische Kommunikation.',
      },
      {
        iconClass: 'icon-[material-symbols--approval-delegation]',
        title: 'Anerkennung und Dokumente',
        text: 'Unterstützung bei Unterlagen, Übersetzungen, Anerkennungsverfahren und Behördenkommunikation.',
      },
      {
        iconClass: 'icon-[material-symbols--home-work]',
        title: 'Relocation',
        text: 'Hilfe bei Einreise, Unterkunft, Anmeldung, Aufenthaltsfragen und den ersten Schritten vor Ort.',
      },
      {
        iconClass: 'icon-[material-symbols--support-agent]',
        title: 'Persönlicher Support',
        text: 'Ein zweisprachiges Team begleitet dich vor, während und nach deiner Ankunft in Deutschland.',
      },
    ],
  },
  costs: {
    eyebrow: 'Für Bewerber kostenlos',
    title: 'Was kostet dich der Weg nach Deutschland?',
    titleHighlight: 'Deutschland',
    text: 'Die Bewerbung und Erstberatung bei Viva Porta ist kostenlos. Wir setzen auf Transparenz: Du bekommst früh eine klare Übersicht, welche Schritte anstehen und welche Kosten von Partnern oder Arbeitgebern getragen werden können.',
    includedTitle: 'Wir koordinieren für dich',
    excludedTitle: 'Keine Hürden im Erstkontakt',
    included: ['Bewerbungscheck', 'Sprach- und Anerkennungsplanung', 'Visa- und Relocation-Fahrplan'],
    excluded: ['Keine Bewerbungsgebühr', 'Keine unklaren Vorauszahlungen', 'Keine versteckten Prozessschritte'],
  },
  recognition: {
    eyebrow: 'Sprache und Anerkennung',
    title: 'Zwei entscheidende Bausteine für deinen Berufseinstieg.',
    titleHighlight: 'Berufseinstieg',
    text: 'Für eine dauerhafte Anstellung in der Pflege sind Deutschkenntnisse und die Anerkennung deiner Ausbildung zentral. Wir führen dich Schritt für Schritt durch beide Themen.',
    cards: [
      {
        iconClass: 'icon-[material-symbols--translate]',
        title: 'Sprachweg',
        text: 'Individuelle Orientierung für Deutschkurse, Prüfungen und Fachsprache im Gesundheitswesen.',
      },
      {
        iconClass: 'icon-[material-symbols--workspace-premium]',
        title: 'Berufsanerkennung',
        text: 'Prüfung deiner Unterlagen und Begleitung bis zur Entscheidung der zuständigen Stelle.',
      },
      {
        iconClass: 'icon-[material-symbols--clinical-notes]',
        title: 'Vorbereitung',
        text: 'Kulturelle Workshops, Interviewtraining und praktische Vorbereitung auf den Alltag im Team.',
      },
    ],
  },
  process: {
    eyebrow: 'Ablauf der Zusammenarbeit',
    title: 'Ein klarer Prozess vom Bedarf bis zum Ankommen.',
    titleHighlight: 'Ankommen',
    text: 'Internationale Personalgewinnung braucht Struktur. Viva Porta macht jeden Schritt nachvollziehbar: vom ersten Gespräch über Auswahl, Vorbereitung und Formalitäten bis zur Integration im Arbeitsalltag.',
    steps: [
      {
        iconClass: 'icon-[material-symbols--fact-check]',
        title: 'Bedarf verstehen',
        text: 'Wir klären Branche, Rolle, Qualifikation, Startzeitpunkt, Sprache, Arbeitsbedingungen und Integrationsmöglichkeiten.',
      },
      {
        iconClass: 'icon-[material-symbols--manage-search]',
        title: 'Passende Kandidat:innen finden',
        text: 'Profile werden fachlich, sprachlich und persönlich vorqualifiziert, damit Unternehmen fundiert entscheiden können.',
      },
      {
        iconClass: 'icon-[material-symbols--school]',
        title: 'Vorbereitung begleiten',
        text: 'Sprache, Rollenverständnis, Erwartungen und notwendige Qualifikationsschritte werden früh strukturiert.',
      },
      {
        iconClass: 'icon-[material-symbols--approval-delegation]',
        title: 'Formalitäten unterstützen',
        text: 'Je nach Branche koordinieren wir Unterlagen, Anerkennung, Visum, Arbeitserlaubnis und relevante Nachweise.',
      },
      {
        iconClass: 'icon-[material-symbols--flight-land]',
        title: 'Ankommen ermöglichen',
        text: 'Einreise, erste Orientierung, Unterkunft und Kommunikation mit dem Arbeitgeber werden planbar vorbereitet.',
      },
      {
        iconClass: 'icon-[material-symbols--handshake]',
        title: 'Langfristig begleiten',
        text: 'Wir bleiben ansprechbar, damit aus einer Vermittlung eine stabile Zusammenarbeit auf beiden Seiten wird.',
      },
    ],
  },
  team: {
    eyebrow: 'Team',
    title: 'Menschen, die Vermittlung persönlich verantworten.',
    titleHighlight: 'persönlich',
    text: 'Der Unterschied zu anonymen Plattformen liegt in der Nähe. Hinter Viva Porta stehen Ansprechpartner:innen, die beide Seiten ernst nehmen: den wirtschaftlichen Druck der Unternehmen und den Lebensweg der Bewerber:innen.',
    members: [
      {
        name: 'Enes Vural',
        role: 'Geschäftsführer und Gesellschafter',
        text: 'Brückenbauer zwischen Deutschland und Türkei, Sozialwirtschaft und unternehmerischer Verantwortung.',
        story:
          'Enes ist in Deutschland aufgewachsen und gehört zur dritten Generation einer Unternehmerfamilie mit türkischen Wurzeln. Seine Familie hat über Jahre soziale Verantwortung im Bildungsbereich getragen. Diese Prägung verbindet er bei Viva Porta mit dem Blick auf den Fachkräftebedarf in Deutschland und die beruflichen Chancen qualifizierter Menschen aus der Türkei.',
        motivation: 'Er versteht sich als kultureller, sprachlicher und menschlicher Brückenbauer. Sein Ziel ist eine Vermittlung, die nicht nur Stellen besetzt, sondern Menschen realistisch und fair in eine neue Zukunft begleitet.',
        image: assets.teamEnes,
        linkedinUrl: 'https://de.linkedin.com/in/enes-vural-1348262b7',
      },
      {
        name: 'Wilhelm Josten',
        role: 'Gesellschafter und Beirat',
        text: 'Unternehmer, Investor und strategischer Mitgestalter für nachhaltige Strukturen.',
        story:
          'Wilhelm stammt aus einer traditionsreichen Unternehmerfamilie und bringt langjährige Erfahrung, Netzwerk und unternehmerisches Know-how ein. Als Gründer der erfolgreichen Einrichtungskette Butlers hat er gezeigt, wie Konzepte, Marktgespür und Kundenorientierung zu nachhaltigem Wachstum führen können.',
        motivation: 'Die Begegnung mit der Familie Vural und der gemeinsame Wille, gesellschaftlich relevante Probleme lösungsorientiert anzugehen, prägen sein Engagement. Sein Fokus liegt auf Qualität, Fairness und Zukunftsfähigkeit.',
        image: assets.teamWilhelm,
        linkedinUrl: 'https://de.linkedin.com/in/wilhelm-josten-6bb14930a',
      },
      {
        name: 'Serife Vural-Banik',
        role: 'Operative Leitung',
        text: 'Zweisprachige operative Leitung mit sozialpädagogischem Hintergrund und kultureller Nähe.',
        story:
          'Serife ist in Deutschland aufgewachsen, absolvierte eine Ausbildung zur Arzthelferin und studierte anschließend Sozialpädagogik. Ihre Erfahrung aus sozialen Arbeitsfeldern und Leitungssituationen bringt sie in Bewerbungsgespräche, Koordination und persönliche Begleitung ein.',
        motivation: 'Durch Sprache, kulturelles Verständnis und operative Sorgfalt baut sie eine wertvolle Brücke für Fachkräfte aus der Türkei und stärkt Vertrauen in kritischen Prozessphasen.',
        image: assets.teamSerife,
      },
      {
        name: 'Murat Vuval',
        role: 'Gesellschafter und Beirat',
        text: 'Begleitet Viva Porta mit unternehmerischem Blick und Fokus auf stabile, faire Partnerschaften.',
        story:
          'Murat bringt Erfahrung aus Verantwortung, Aufbauarbeit und Zusammenarbeit mit Menschen ein. Sein Blick hilft dabei, Chancen realistisch einzuschätzen und Partnerschaften so zu denken, dass sie für beide Seiten dauerhaft funktionieren.',
        motivation: 'Ihm ist wichtig, dass Vermittlung nicht bei der Zusage endet, sondern zu echter Teilhabe und verlässlichen Arbeitsbeziehungen führt.',
        image: assets.teamMurat,
        linkedinUrl: 'https://www.linkedin.com/in/murat-v-113bb912/',
      },
      {
        name: 'Dr. Kirstin Wingler',
        role: 'Assistenz der Geschäftsführung',
        text: 'Assistenz der Geschäftsführung mit internationaler Forschungserfahrung und Projektmanagement-Praxis.',
        story:
          'Dr. Kirstin Wingler bringt internationale Erfahrung in biomedizinischer Forschung und Lehre mit. Nach ihrem Abschluss als Diplom-Ernährungswissenschaftlerin promovierte sie am Deutschen Institut für Ernährungsforschung und arbeitete in Startup- und Universitätskontexten, unter anderem in Australien und den Niederlanden.',
        motivation: 'Ihre medizinischen Fachkenntnisse und Erfahrung im Projektmanagement helfen dabei, Qualifikationen, Unterlagen und Anforderungen sauber einzuordnen und verlässliche Vermittlungsprozesse aufzubauen.',
        image: assets.teamKirstin,
        linkedinUrl: 'https://www.linkedin.com/in/kirstin-w-43264a4/',
      },
      {
        name: 'Ergün Yalcin',
        role: 'Verwaltung',
        text: 'Sorgt im Hintergrund für Ordnung, Verlässlichkeit und saubere administrative Abläufe.',
        story:
          'Ergün unterstützt die Verwaltung dort, wo Genauigkeit und Ruhe entscheidend sind: Daten, Unterlagen, Abstimmungen und organisatorische Nachverfolgung. Damit hält er den Prozess stabil, auch wenn viele Schritte parallel laufen.',
        motivation: 'Sein Anspruch ist, dass Bewerber:innen und Partner sich auf klare Abläufe verlassen können, weil die administrative Basis stimmt.',
        image: assets.teamErgun,
      },
    ],
  },
  story: {
    eyebrow: 'Stimmen aus der Zusammenarbeit',
    title: 'Partnerschaft, die Menschen ins Team bringt.',
    titleHighlight: 'Menschen',
    text: 'Was Partner an Viva Porta schätzen: direkte Kommunikation, verlässliche Vorbereitung und ein klares Verständnis der Rollen – besonders dort, wo Globus Fachkräfte für Frischebereiche und Service gewinnt.',
    quote: 'Hier kann ein echtes Kunden- oder Kandidatenzitat stehen. Der Bereich ist bewusst vorbereitet, damit spätere Stimmen glaubwürdig und vollständig im Editor gepflegt werden können.',
    author: 'Platzhalter für echtes Testimonial',
    role: 'Bitte im Editor durch freigegebenes Zitat ersetzen',
    partnerLogo: assets.partner,
    partnerSecondaryLogo: assets.partnerSecondary,
    testimonials: [
      {
        quote: 'Die Viva Porta ist ein außerordentlich engagierter Geschäftspartner, welcher über hervorragende Kontakte in den türkischen Arbeitsmarkt verfügt.',
        author: 'Gerd Schneider',
        role: 'Geschäftsführer, tempusleben',
        image: teamGerdSchneider,
        imageAlt: 'Gerd Schneider, Geschäftsführer von tempusleben',
        logo: partnerTempusleben,
        logoAlt: 'tempusleben Logo',
      },
      {
        quote: 'Gute Zusammenarbeit beginnt dort, wo Menschen vorbereitet, verstanden und zuverlässig bis in den Arbeitsalltag begleitet werden.',
        author: 'Viva Porta Partnernetzwerk',
        role: 'Vorbereitung und Integration',
        image: heroUnternehmenIntegration,
        imageAlt: 'Internationale Fachkraft wird im Team begrüßt',
        logo: partnerEmek,
        logoAlt: 'EMEK Logo',
      },
      {
        quote: 'Im Einzelhandel zählt, dass Qualifikation, Tempo und Team zusammenpassen. Für Globus bereiten wir Fachkräfte gezielt auf Rollen in Frischebereichen und Service vor.',
        author: 'Viva Porta × Globus',
        role: 'Partnerschaft im Einzelhandel',
        image: heroHandel,
        imageAlt: 'Mitarbeiterin in einem modernen Einzelhandelsgeschäft',
        logo: partnerGlobus,
        logoAlt: 'Globus Logo',
      },
    ],
  },
  partners: {
    eyebrow: 'Partner & Netzwerk',
    title: 'Vertrauen, das auf echten Partnerschaften beruht.',
    titleHighlight: 'Partnerschaften',
    text: 'Ausgewählte Unternehmen und Netzwerke in Deutschland und der Türkei, die unsere Arbeit tragen.',
    trustLabel: 'Mitglied im GVP – Gesamtverband der Personaldienstleister',
    items: [
      {
        name: 'Globus',
        logo: partnerGlobus,
        alt: 'Globus Logo',
        description:
          'Für Vollsortimenter wie Globus gewinnen wir Fachkräfte für die Frischebereiche – Fachmetzgerei, Fisch- und Käsetheke. Genau dort, wo Beratung Frequenz bringt, Margen ermöglicht und Kundinnen und Kunden langfristig bindet.',
        href: '/globus',
      },
      {
        name: 'tempusleben',
        logo: partnerTempusleben,
        alt: 'tempusleben Logo',
        description: 'tempusleben ist ein ambulanter und intensivpflegerischer Pflegedienst. Die Zusammenarbeit zeigt, wie wichtig verlässliche, menschlich begleitete Fachkräftegewinnung gerade in sensiblen Pflegebereichen ist.',
        href: 'https://tempusleben.de/',
      },
      {
        name: 'EMEK',
        logo: partnerEmek,
        alt: 'EMEK Logo',
        description: 'Die EMEK GmbH ist ein Pflegedienst. Als Partner aus dem Pflegeumfeld steht EMEK für praktisches Verständnis, Nähe zum Arbeitsalltag und ein Netzwerk, das Menschen nicht nur vermittelt, sondern vorbereitet.',
        href: 'https://emek-pflegedienst.de/',
      },
    ],
  },
  globusPartner: {
    eyebrow: 'Partner im Einzelhandel',
    title: 'Gemeinsam mit Globus in den Einzelhandel.',
    text: 'Globus steht für große, serviceorientierte Märkte mit starken Frischebereichen. Gemeinsam schaffen wir einen Rahmen, in dem internationale Talente vorbereitet, begleitet und in Teams integriert werden können – mit klaren Erwartungen, persönlicher Betreuung und Blick auf langfristige Zusammenarbeit.',
    image: groupPhoto,
    imageAlt: 'Viva Porta zu Besuch im Metzgereibereich einer GLOBUS Markthalle',
    logo: partnerGlobus,
    logoAlt: 'Globus Logo',
    primaryAction: { label: 'Einzelhandel ansehen', href: '/branchen/einzelhandel' },
  },
  info: {
    eyebrow: 'Weiterführende Informationen',
    title: 'Alles Wichtige zu Einwanderung, Anerkennung und Pflegeberuf.',
    titleHighlight: 'Pflegeberuf',
    links: [
      { label: 'Anerkennung in Deutschland', href: 'https://www.anerkennung-in-deutschland.de/' },
      { label: 'Make it in Germany', href: 'https://www.make-it-in-germany.com/' },
      { label: 'Bundesagentur für Arbeit', href: 'https://www.arbeitsagentur.de/' },
    ],
  },
  contact: {
    eyebrow: 'Bereit für den nächsten Schritt?',
    title: 'Bewirb dich als Pflegefachkraft für Deutschland.',
    titleHighlight: 'Deutschland',
    text: 'Schick uns deine Anfrage. Wir melden uns mit einer realistischen Einschätzung und den nächsten Schritten für deinen Weg.',
    primaryAction: { label: 'Kostenlos bewerben', href: contact.application },
    secondaryAction: { label: 'E-Mail schreiben', href: contact.mail },
    address: 'Viva Porta GmbH, Deutschland',
    email: 'info@vivaporta.org',
    hours: 'Mo - Fr: 9.00 - 17.00 Uhr',
  },
  requirements: {
    eyebrow: 'Voraussetzungen',
    title: 'Das brauchst du für deinen Start in Deutschland.',
    titleHighlight: 'Deutschland',
    text: 'Du musst nicht alles allein schaffen. Du bringst deine Ausbildung und Motivation mit – den Rest organisieren wir gemeinsam, Schritt für Schritt.',
    columns: [
      {
        iconClass: 'icon-[material-symbols--person-check]',
        title: 'Was du mitbringst',
        items: [
          'Abgeschlossene Pflegeausbildung oder ein Pflege-Studium aus der Türkei',
          'Motivation, Deutsch bis mindestens Niveau B1/B2 zu lernen',
          'Bereitschaft, für deinen neuen Beruf nach Deutschland zu ziehen',
          'Gültiger Reisepass und vollständige Ausbildungsnachweise',
        ],
      },
      {
        iconClass: 'icon-[material-symbols--volunteer-activism]',
        title: 'Was wir für dich übernehmen',
        items: [
          'Kostenlose Deutschkurse bis zum geprüften Sprachniveau',
          'Anerkennung deines Abschlusses inkl. Übersetzungen und Behörden',
          'Visum, Einreise und die Suche nach einer ersten Wohnung',
          'Persönliche Begleitung vom Erstgespräch bis nach deiner Ankunft',
        ],
      },
    ],
  },
  faqApplicant: {
    eyebrow: 'FAQ für Pflegekräfte',
    title: 'Die wichtigsten Fragen vor deiner Bewerbung.',
    titleHighlight: 'Bewerbung',
    text: 'Wenn deine Frage hier nicht beantwortet wird, ruf uns einfach an – wir beraten dich kostenlos und auf Türkisch.',
    items: [
      {
        question: 'Was kostet mich die Vermittlung?',
        answer: 'Den Großteil der Gebühren zahlt dein Arbeitgeber für dich. Das bedeutet: Für dich ist die Vermittlung grundsätzlich kostenlos. Je nach Beruf und Branche können jedoch Beratungsgebühren anfallen. Wenn du mehr dazu wissen möchtest, kontaktiere uns gerne.',
      },
      {
        question: 'Welches Sprachniveau brauche ich?',
        answer: 'Natürlich setzt ein Arbeitgeber in Deutschland voraus, dass du langfristig Deutsch sprichst, da du mit deinen Kolleg:innen und Kund:innen kommunizieren musst. Je nach Beruf kannst du jedoch auch ohne perfekte Deutschkenntnisse nach Deutschland einreisen. Achtung: Pflegekräfte benötigen für das Visum mindestens das Sprachniveau B1.',
      },
      {
        question: 'Wird mein türkischer Abschluss anerkannt?',
        answer: 'Wir begleiten dich während des gesamten Anerkennungsverfahrens und prüfen vorab, ob und unter welchen Voraussetzungen dein Abschluss in Deutschland anerkannt werden kann.',
      },
      {
        question: 'Wie lange dauert der gesamte Weg?',
        answer: 'Leider gibt es darauf keine pauschale Antwort, da die Dauer stark von den zuständigen Behörden abhängt. Die Bearbeitungszeiten unterscheiden sich zwischen den einzelnen Bundesländern und jedes Verfahren ist individuell. Erfahrungsgemäß dauert es je nach Beruf etwa drei bis zwölf Monate, bis du nach Deutschland einreisen kannst.',
      },
      {
        question: 'Bekomme ich vorher einen Arbeitsvertrag?',
        answer: 'Selbstverständlich. Ein Arbeitsvertrag ist eine wichtige Voraussetzung, um das Anerkennungsverfahren zu starten und ein Visum zu beantragen.',
      },
      {
        question: 'Helft ihr nach der Einreise auch bei Behördengängen und der ersten Unterkunft?',
        answer: 'Ja. Wir begleiten dich bei den ersten Behördengängen sowie beim Abschluss wichtiger Versicherungen und Verträge. Außerdem unterstützen wir dich bei der Wohnungssuche.',
      },
    ],
  },
  faqCompany: {
    eyebrow: 'FAQ für Arbeitgeber',
    title: 'Antworten für Kliniken und Pflegeeinrichtungen.',
    titleHighlight: 'Pflegeeinrichtungen',
    text: 'Sie haben weitere Fragen zu Ablauf, Kosten oder Recht? Sprechen Sie direkt mit unserem Team.',
    items: [
      {
        question: 'Was kostet die Vermittlung?',
        answer: 'Sie zahlen erfolgsbasiert – Kosten entstehen erst bei erfolgreicher Vermittlung. Keine versteckten Gebühren, ein transparentes Modell auf Augenhöhe.',
      },
      {
        question: 'Welche Qualifikation haben die Pflegekräfte?',
        answer: 'Alle Kandidaten sind examinierte Pflegekräfte mit strukturierter Vorauswahl, geprüftem Sprachniveau B1/B2 und einem laufenden oder abgeschlossenen Anerkennungsverfahren.',
      },
      {
        question: 'Übernehmen Sie Anerkennung und Bürokratie?',
        answer: 'Ja. Wir managen Unterlagen, Übersetzungen, Anerkennungsantrag, Visum und Arbeitserlaubnis vollständig und entlasten Ihr Team spürbar.',
      },
      {
        question: 'Wie lange dauert es bis zum Arbeitsbeginn?',
        answer: 'Üblicherweise 12 bis 18 Monate ab Auftrag – inklusive Sprache, Anerkennung und Einreise. Sie erhalten feste Ansprechpartner und transparente Zeitpläne.',
      },
      {
        question: 'Was passiert nach der Einreise?',
        answer: 'Wir begleiten Onboarding und Integration über den ersten Arbeitstag hinaus und bleiben Ansprechpartner für einen nachhaltig gelungenen Einstieg.',
      },
      {
        question: 'Warum Pflegekräfte aus der Türkei?',
        answer: 'Die Türkei bildet Pflegekräfte auf hohem Niveau aus. Kulturelle Nähe, eine große Community in Deutschland und kurze Direktflüge sorgen für schnelleres Ankommen und eine hohe Bleibequote.',
      },
    ],
  },
  quality: {
    eyebrow: 'Qualität und Integration',
    title: 'Faire Vermittlung, die nachhaltig im Team ankommt.',
    titleHighlight: 'nachhaltig',
    text: 'Erfolgreiche Vermittlung endet für uns nicht am Flughafen. Geprüfte Qualität und echte Integration sorgen dafür, dass Ihre neuen Pflegekräfte langfristig bleiben.',
    stats: [
      { value: 'Langfristig', label: 'Zusammenarbeit mit Blick auf stabile Perspektiven' },
      { value: 'Vorbereitet', label: 'Sprachliche und fachliche Einordnung vor dem Start' },
      { value: 'Begleitet', label: 'Ansprechbar in wichtigen Phasen des Ankommens' },
    ],
    points: [
      {
        iconClass: 'icon-[material-symbols--verified]',
        title: 'Faire Anwerbung',
        text: 'Wir orientieren uns an anerkannten Grundsätzen verantwortungsvoller Anwerbung und legen Wert auf transparente, nachvollziehbare Prozesse.',
      },
      {
        iconClass: 'icon-[material-symbols--diversity-3]',
        title: 'Kulturelle Integration',
        text: 'Interkulturelle Workshops und persönliche Betreuung helfen den Fachkräften, im Team und im Alltag schnell anzukommen.',
      },
      {
        iconClass: 'icon-[material-symbols--health-and-safety]',
        title: 'Qualitätsgeprüft',
        text: 'Fachliche Eignung, Sprachzertifikat und Anerkennungsverfahren werden vor der Vermittlung nachvollziehbar dokumentiert.',
      },
      {
        iconClass: 'icon-[material-symbols--handshake]',
        title: 'Langfristige Bindung',
        text: 'Nachbetreuung und Onboarding-Begleitung senken Fluktuation und sichern den nachhaltigen Erfolg der Einstellung.',
      },
    ],
  },
  knowledge: {
    eyebrow: 'Wissen und Ratgeber',
    title: 'Gut informiert auf dem Weg nach Deutschland.',
    titleHighlight: 'informiert',
    text: 'Antworten und Orientierung zu den wichtigsten Themen rund um Anerkennung, Sprache und das Leben in Deutschland.',
    articles: [
      {
        iconClass: 'icon-[material-symbols--workspace-premium]',
        eyebrow: 'Anerkennung',
        title: 'Anerkennung in Deutschland',
        text: 'Wie dein türkischer Pflegeabschluss anerkannt wird – von der Antragstellung bis zum Bescheid.',
        linkLabel: 'Mehr erfahren',
        href: 'https://www.anerkennung-in-deutschland.de/',
      },
      {
        iconClass: 'icon-[material-symbols--translate]',
        eyebrow: 'Sprache',
        title: 'Deutsch lernen für Pflegekräfte',
        text: 'Vom Alltagsdeutsch bis zur medizinischen Fachsprache – so erreichst du dein Sprachziel.',
        linkLabel: 'Mehr erfahren',
        href: '/ablauf',
      },
      {
        iconClass: 'icon-[material-symbols--home-work]',
        eyebrow: 'Leben',
        title: 'Leben und Arbeiten in Deutschland',
        text: 'Wohnen, Behörden, Gehalt und Alltag: Was dich in deinem neuen Zuhause erwartet.',
        linkLabel: 'Mehr erfahren',
        href: 'https://www.make-it-in-germany.com/',
      },
      {
        iconClass: 'icon-[material-symbols--balance]',
        eyebrow: 'Fairness',
        title: 'Faire Vermittlung',
        text: 'Woran du eine faire, seriöse Vermittlung erkennst – und worauf du achten solltest.',
        linkLabel: 'Mehr erfahren',
        href: '/ueber-vivaporta',
      },
    ],
  },
  values: {
    eyebrow: 'Mission',
    title: 'Unsere Mission ist eine menschliche Antwort auf ein strukturelles Problem.',
    titleHighlight: 'menschliche Antwort',
    text: 'Viva Porta möchte Fachkräftemangel in Deutschland und die Motivation internationaler Menschen sinnvoll zusammenführen. Wir verstehen uns als Brücke zwischen Unternehmen und Menschen, die hier beruflich Fuß fassen möchten.',
    items: [
      {
        iconClass: 'icon-[material-symbols--handshake]',
        title: 'Vertrauen',
        text: 'Klare Kommunikation, transparente Schritte und feste Ansprechpartner schaffen Sicherheit auf beiden Seiten.',
      },
      {
        iconClass: 'icon-[material-symbols--diversity-1]',
        title: 'Mensch im Mittelpunkt',
        text: 'Hinter jedem Bedarf und jeder Bewerbung stehen Menschen mit Erwartungen, Verantwortung und Zukunftsplänen.',
      },
      {
        iconClass: 'icon-[material-symbols--verified-user]',
        title: 'Nachhaltigkeit',
        text: 'Eine gute Vermittlung endet nicht mit dem Vertrag. Sie zeigt sich daran, ob Zusammenarbeit langfristig trägt.',
      },
    ],
    bridgeTitle: 'Warum Viva Porta als Brücke?',
    bridgeText:
      'Deutschland braucht verlässliche Fachkräfte. Gleichzeitig gibt es international viele Menschen, die hier arbeiten, lernen und ankommen möchten. Viva Porta bringt diese Interessen nicht abstrakt zusammen, sondern persönlich begleitet, fair und mit Blick auf langfristige Integration.',
    bridgeAction: { label: 'Warum der Mensch im Mittelpunkt steht', href: '/ueber-vivaporta' },
  },
  consultationApplicant: {
    eyebrow: 'Erstberatung anfragen',
    title: 'Starte mit einem persönlichen Gespräch.',
    titleHighlight: 'persönlichen',
    text: 'Erzähl uns von deinem Ziel. In einem kostenlosen Erstgespräch – gerne auf Türkisch – klären wir deine Voraussetzungen und die nächsten Schritte.',
    phoneLabel: 'Direkt anrufen',
    phoneNumber: 'tel:+4917642887719',
    phoneDisplay: '+49 176 42887719',
    primaryAction: { label: 'Jetzt anrufen', href: 'tel:+4917642887719' },
    secondaryAction: { label: 'Online anmelden', href: contact.application },
    trustLabel: 'Kostenlos und unverbindlich · Antwort innerhalb von 24 Stunden',
  },
  consultationCompany: {
    eyebrow: 'Für Unternehmen',
    title: 'Besprechen Sie Ihren Fachkräftebedarf.',
    titleHighlight: 'Personalbedarf',
    text: 'Sagen Sie uns, in welcher Branche und für welche Rollen Sie Unterstützung suchen. Wir geben eine realistische Einschätzung zu Profilen, Prozess und nächstem Schritt.',
    phoneLabel: 'Direkt anrufen',
    phoneNumber: 'tel:+4917642887719',
    phoneDisplay: '+49 176 42887719',
    primaryAction: { label: 'Jetzt anrufen', href: 'tel:+4917642887719' },
    secondaryAction: { label: 'Kontakt aufnehmen', href: '/kontakt' },
    trustLabel: 'Erfolgsbasiert · Feste Ansprechpartner · Antwort innerhalb von 24 Stunden',
  },
  footer: {
    text: 'Viva Porta verbindet Unternehmen in Deutschland mit internationalen Menschen, die hier beruflich ankommen möchten. Persönlich, seriös und langfristig gedacht.',
    links: [
      { label: 'Bewirb dich jetzt', href: '/bewerben' },
      { label: 'Arbeiten bei Globus', href: '/globus' },
      { label: 'Unser Team', href: '/team' },
      { label: 'Mission', href: '/ueber-vivaporta' },
      { label: 'Kontakt', href: '/kontakt' },
      { label: 'Startseite', href: '/' },
      { label: 'Pflege', href: '/branchen/pflege' },
      { label: 'Einzelhandel', href: '/branchen/einzelhandel' },
      { label: 'Handwerk', href: '/branchen/handwerk' },
      { label: 'Impressum', href: '/impressum' },
    ],
    socialLinks: [{ label: 'Instagram', href: 'https://www.instagram.com/' }],
  },
};
